import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import dayjs from "dayjs";
import { useLocation, useParams } from "react-router";
import useApi from "../../../../core/customhook/useApi";
import webservice_url, {
  apibaseurl,
} from "../../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../../types/common-type";
import Datatable from "../../../../core/common/dataTable";
import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
import Loading from "../../../../core/common/custom-loading/Loading";
import Footer from "../../../../core/common/Footer";
import { formattedDate } from "../../../../core/methods/formattedDate";
import {
  parseDate,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import PdfIcon from "../../../../style/icon/svg/PdfIcon";
import { PageLoader } from "../../../../core/common/loader/PageLoader";
import AddressDetailsView from "../hr-pending-action/Addressdetailsview";

interface DocumentItem {
  document_id: string;
  file_name: string;
  original_name: string;
  file_path: string;
  mime_type: string;
  uploaded_at: string;
}

interface Artifact {
  case_artifact_id: string;
  assignment_template_id: string;
  assignment_id: string;
  case_id: number;
  remarks: string;
  followup_date: string;
  created_by: string;
  created_at: string;
  documents: DocumentItem[];
}

interface ArtifactsApiResponse {
  success: boolean;
  data: Artifact[];
  pagination?: {
    totalRecords: number;
  };
}

// One form entry (single check = 1 entry, multiple check = many entries)
interface FormEntry {
  entry_index: number;
  fields: any[];
}

// breadcrumb data
const breadcrumbItem = [{ label: "Case Details" }, { label: "View Artifacts" }];

// Field types that are shown as read-only inputs
const VISIBLE_FIELD_TYPES = ["text", "number", "dropdown", "date", "email"];

/**
 * API can return the form in two shapes:
 *  - data.fields  -> single entry
 *  - data.entries -> [{ entry_index, fields: [] }, ...] (single or multiple)
 * This normalises both into an array of entries.
 */
const getFormEntries = (data: any): FormEntry[] => {
  if (!data) return [];

  if (Array.isArray(data.entries) && data.entries.length > 0) {
    return data.entries
      .filter((entry: any) => Array.isArray(entry?.fields))
      .map((entry: any, index: number) => ({
        entry_index: entry.entry_index ?? index,
        fields: entry.fields,
      }));
  }

  if (Array.isArray(data.fields) && data.fields.length > 0) {
    return [{ entry_index: 0, fields: data.fields }];
  }

  return [];
};

/** Dropdown -> option label, date -> formatted date, everything else -> value */
const getFieldDisplayValue = (field: any): string => {
  const raw = field?.value;

  if (raw === null || raw === undefined || raw === "") return "";

  if (field?.field_type === "dropdown" && Array.isArray(field?.options)) {
    const match = field.options.find(
      (opt: any) => String(opt?.value) === String(raw),
    );
    if (match?.label) return String(match.label);
  }

  if (field?.field_type === "date") {
    const parsed = dayjs(raw);
    if (parsed.isValid()) return parsed.format("DD MMM YYYY");
  }

  return String(raw);
};

// DetailItem
interface DetailItemProps {
  label: string;
  value?: string | number | null;
  placeholder?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({
  label,
  value = "",
  placeholder = "",
}) => {
  const stringValue = String(value ?? "");
  const isEmpty = stringValue.trim() === "";

  return (
    <div className="col-md-4 mb-3">
      <label className="form-label fw-semibold" title={label}>
        {label}
      </label>
      <input
        type="text"
        className="form-control"
        value={isEmpty ? "" : stringValue}
        placeholder={isEmpty ? placeholder || "—" : ""}
        disabled
        title={stringValue}
      />
    </div>
  );
};

const AddArtifacts: React.FC = () => {
  const {
    case_id: caseId,
    assignment_id: assignId,
    assignment_template_id: assingTempId,
  } = useLocation()?.state || {};

  const [formData, setFormData] = useState<any>({});

  const [docsInfo, setDocsInfo] = useState<any[]>([]);

  const [allItemList, setAllItemList] = useState<Artifact[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentItem[]>(
    [],
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [addressData, setAddressData] = useState<any[]>([]);
  const [templateInfo, setTemplateInfo] = useState<any>(null);

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  // API
  const { callApi: getCaseDetailsAPI, loading: loading_case } = useApi<any>(
    "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const { callApi: getArtifactsAPI, loading } = useApi<ArtifactsApiResponse>(
    "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const getTemplateDocument = useApi("", {
    autoCall: false,
    method: "GET",
    handleGlobalError: false,
  });

  // ========== fetch Check data ===========
  const fetchCheckData = async () => {
    try {
      const res = await getCaseDetailsAPI(
        undefined,
        `${webservice_url.get_insufficiency_details}/${assingTempId}`,
      );
      if (res?.success) {
        const data = res?.data || {};
        setFormData(data);
        // Store response details except fields
        setTemplateInfo({
          response_id: data.response_id,
          assignment_template_id: data.assignment_template_id,
          is_draft: data.is_draft,
          submitted_at: data.submitted_at,
          updated_at: data.updated_at,
          check_name: data.check_name,
          form_type: data.form_type,
        });
        setAddressData(Array.isArray(data.data) ? data.data : []);
      }
    } catch (err: any) {
      console.error(err?.response);
    }
  };
  useEffect(() => {
    if (!assingTempId) return;
    fetchCheckData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getArtifactsAPI(
        undefined,
        `${webservice_url.get_all_artifacts}/${assingTempId}`,
      );

      if (res?.success) {
        const data = Array.isArray(res?.data) ? res.data : [];

        setAllItemList(data);

        setPagination((prev) => ({
          ...prev,
          total: res?.pagination?.totalRecords ?? data.length,
        }));
      } else {
        setAllItemList([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (err: any) {
      console.error(err?.response);

      // Always keep Datatable dataSource as an array
      setAllItemList([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    }
  };
  useEffect(() => {
    if (!assingTempId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, caseId]);

  const handleViewDocuments = (documents: DocumentItem[]) => {
    setSelectedDocuments(documents || []);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDocuments([]);
  };

  const handleViewDocument = (doc: DocumentItem) => {
    let doc_link = doc?.file_path;
    if (!doc_link) return;
    window.open(doc_link, "_blank", "noopener,noreferrer");
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    }));
  };
  // ========== fetch data ===========

  // Fetch document
  const getTemplateDocs = async () => {
    try {
      const res = await getTemplateDocument.callApi(
        undefined,
        `/api/v1/insufficiency/documents/${assingTempId}`,
      );

      if (res?.success) {
        setDocsInfo(res.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (!assingTempId) return;
    getTemplateDocs();
  }, [assingTempId]);

  const columns: ColumnsType<Artifact> = [
    {
      title: "Case ID",
      dataIndex: "case_id",
      width: 120,
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      onCell: () => cellWidth(400),
      render: (remarks: string) =>
        remarks ? remarks : <span className="text-muted">-</span>,
      sorter: (a: any, b: any) => safeLocaleCompare(a, b, "remarks"),
    },
    {
      title: "Follow-up Date",
      dataIndex: "followup_date",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.followup_date).getTime() -
        parseDate(b.followup_date).getTime(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => formattedDate(date),
      sorter: (a: any, b: any) =>
        parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime(),
    },
    {
      title: "Documents",
      width: 160,
      render: (_: unknown, record: Artifact) => {
        const count = record.documents?.length || 0;
        if (count === 0) {
          return <span className="text-muted">No documents</span>;
        }
        return (
          <button
            className="btn btn-sm btn-tertiary"
            onClick={() => handleViewDocuments(record.documents)}
          >
            <i className="ti ti-eye"></i> View
            {/* ({count}) */}
          </button>
        );
      },
    },
  ];

  // AddressDetailsView sirf "Address Verification" check par dikhega
  const isAddressCheck =
    templateInfo?.check_name?.trim().toLowerCase() === "address verification";

  // Uploaded Documents card tabhi dikhega jab documents hon (aur address check na ho)
  const showDocsCard =
    !!templateInfo?.check_name && !isAddressCheck && docsInfo?.length > 0;

  // Single (fields) + Multiple (entries) dono ke liye normalised entries
  const formEntries = useMemo(() => getFormEntries(formData), [formData]);
  const isMultipleEntries = formEntries.length > 1;

  return (
    <div className="page-wrapper">
      <div className="content px-2 px-md-4 pb-5">
        {loading_case || loading ? (
          <PageLoader />
        ) : (
          <>
            {/* PageBreadcrumb */}
            <PageBreadcrumb
              title="View Artifacts"
              breadcrumbs={breadcrumbItem}
              showBackButton={true}
              backRoute={-1}
              // backRoute={`${all_routes.admin_caseDetails}/${caseId}`}
            />
            {/* /PageBreadcrumb */}

            <>
              {/* artiface history */}
              <div className="card mt-3">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h4 className="fw-bold mb-0">Artifacts History</h4>

                  {!loading && (
                    <span className="badge bg-light text-dark border px-3 py-2">
                      {pagination.total}{" "}
                      {pagination.total === 1 ? "record" : "records"}
                    </span>
                  )}
                </div>

                {/* table */}
                <div className="card-body">
                  {loading ? (
                    <div style={{ padding: "40px 0" }}>
                      <Loading />
                    </div>
                  ) : (
                    <Datatable
                      rowKey="case_artifact_id"
                      columns={columns}
                      dataSource={allItemList}
                      hideSizeChanger={true}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                      }}
                      handleTableChange={handleTableChange}
                    />
                  )}
                </div>
              </div>

              {/* case details */}
              <div className="card mt-3">
                <div className="card-header bg-white py-3 border-bottom">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                        style={{ width: 40, height: 40 }}
                      >
                        <i className="ti ti-file-text text-warning fs-3"></i>
                      </div>

                      <div>
                        <h6 className="fw-bold mb-0">{formData?.check_name}</h6>
                        <small className="text-muted">
                          Last Updated:{" "}
                          {dayjs(formData?.updated_at).format(
                            "DD MMM YYYY, hh:mm A",
                          )}
                        </small>
                      </div>
                    </div>

                    {/* <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle px-3 py-2">
                                        Cleared
                                    </span> */}
                  </div>
                </div>

                {isAddressCheck ? (
                  <AddressDetailsView data={addressData} />
                ) : (
                  <div className="card-body">
                    {loading_case ? (
                      <Loading />
                    ) : formEntries.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        No Data Available
                      </div>
                    ) : (
                      formEntries.map((entry, entryIdx) => {
                        const visibleFields = (entry.fields || [])
                          .filter((f: any) =>
                            VISIBLE_FIELD_TYPES.includes(f?.field_type),
                          )
                          .sort(
                            (a: any, b: any) =>
                              (a?.display_order ?? 0) - (b?.display_order ?? 0),
                          );

                        return (
                          <div
                            key={entry.entry_index ?? entryIdx}
                            className={
                              isMultipleEntries &&
                              entryIdx !== formEntries.length - 1
                                ? "mb-4"
                                : ""
                            }
                          >
                            {/* Sub heading sirf tab jab multiple entries ho */}
                            {isMultipleEntries && (
                              <div
                                className="px-3 py-2 mb-3 rounded"
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#344054",
                                  background: "#F9FAFB",
                                  border: "1px solid #EAECF0",
                                }}
                              >
                                {formData?.check_name} ({entryIdx + 1} of{" "}
                                {formEntries.length})
                              </div>
                            )}

                            <div className="row gx-3">
                              {visibleFields.map((d: any) => (
                                <DetailItem
                                  key={`${entry.entry_index}-${d?.field_id}`}
                                  label={d?.label}
                                  value={getFieldDisplayValue(d)}
                                  placeholder={d?.placeholder}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
 
              {showDocsCard ? (
                <div className="card mt-3">
                  <div className="card-header bg-white py-3 border-bottom">
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
                        style={{ width: 40, height: 40 }}
                      >
                        <i className="ti ti-file-text text-warning fs-3"></i>
                      </div>

                      <div>
                        <h6 className="fw-bold mb-0">Uploaded Documents</h6>
                        <small className="text-muted">
                          Candidate uploaded supporting documents
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {docsInfo.map((item) => (
                        <div
                          className="col-xl-3 col-lg-4 col-md-6"
                          key={item.file_id}
                        >
                          <div
                            className="card h-100 border-0 shadow-sm"
                            style={{
                              borderRadius: "14px",
                              transition: "0.25s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform =
                                "translateY(-4px)";
                              e.currentTarget.style.boxShadow =
                                "0 8px 20px rgba(0,0,0,0.12)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "";
                            }}
                          >
                            <div className="card-body text-center">
                              {/* Icon */}
                              <div
                                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: 70,
                                  height: 70,
                                  borderRadius: "50%",
                                  background: "#FDECEC",
                                }}
                              >
                                <PdfIcon size={42} color="#D32F2F" />
                              </div>

                              {/* Document Name */}
                              <h6
                                className="fw-bold text-capitalize mb-2"
                                title={item.field_key.replaceAll("_", " ")}
                              >
                                {item.field_key.replaceAll("_", " ")}
                              </h6>

                              {/* File Name */}
                              <div className="small text-muted text-truncate mb-3">
                                {item.original_name}
                              </div>

                              {/* Info */}
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                  {item.mime_type?.split("/")[1]?.toUpperCase()}
                                </span>

                                <span className="badge bg-light text-dark border">
                                  {new Date(item.uploaded_at).toLocaleDateString()}
                                </span>
                              </div>

                              {/* View Button */}
                              <a
                                href={`${apibaseurl}/uploads/${item.s3_key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-primary w-100 mt-2"
                              >
                                <i className="ti ti-eye me-1"></i>
                                View Document
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          </>
        )}

        {/* view documents modal */}
        <Modal
          title={
            <span className="fw-bold">
              <i className="ti ti-folder-open me-2 text-primary"></i>
              Documents
            </span>
          }
          open={modalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={700}
        >
          {selectedDocuments.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="ti ti-folder-off text-secondary"
                style={{ fontSize: "48px" }}
              ></i>

              <h6 className="mt-3 mb-1">No Documents Found</h6>

              <p className="text-muted mb-0">No documents available</p>
            </div>
          ) : (
            <div className="row g-3">
              {selectedDocuments.map((doc) => (
                <div className="col-md-6" key={doc.document_id}>
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{
                      borderRadius: "14px",
                      transition: "0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div className="card-body">
                      <div className="d-flex">
                        {/* Icon */}
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
                          style={{
                            width: 55,
                            height: 55,
                            flexShrink: 0,
                          }}
                        >
                          <i className="ti ti-file-text text-danger fs-3"></i>
                        </div>

                        {/* Details */}
                        <div className="flex-grow-1">
                          <h6
                            className="fw-bold mb-1 text-truncate"
                            title={doc.original_name}
                          >
                            {doc.original_name}
                          </h6>

                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-muted small">Type:</span>
                            <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                              {doc.mime_type}
                            </span>
                          </div>

                          <div className="text-muted small mb-2">
                            Uploaded: {formattedDate(doc.uploaded_at)}
                          </div>
                          <a
                            href={`${apibaseurl}/uploads/${doc.file_name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary"
                          >
                            <i className="ti ti-eye me-1"></i>
                            View
                          </a>
                          {/* <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleViewDocument(doc)}
                                                    >
                                                        View
                                                    </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      </div>
      <Footer />
    </div>
  );
};

export default AddArtifacts;


// import React, { useEffect, useState } from "react";
// import { Modal } from "antd";
// import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
// import dayjs from "dayjs";
// import { useLocation, useParams } from "react-router";
// import useApi from "../../../../core/customhook/useApi";
// import webservice_url, {
//   apibaseurl,
// } from "../../../../core/webservice/Webservice_url";
// import { PaginationType } from "../../../../types/common-type";
// import Datatable from "../../../../core/common/dataTable"; 
// import { PageBreadcrumb } from "../../../../core/common/BreadCrumbForm";
// import Loading from "../../../../core/common/custom-loading/Loading";
// import Footer from "../../../../core/common/Footer";
// import { formattedDate } from "../../../../core/methods/formattedDate";
// import {
//   parseDate,
//   safeLocaleCompare,
// } from "../../../../core/methods/sorterMethods";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import PdfIcon from "../../../../style/icon/svg/PdfIcon";
// import { PageLoader } from "../../../../core/common/loader/PageLoader";
// import AddressDetailsView from "../hr-pending-action/Addressdetailsview";

// interface DocumentItem {
//   document_id: string;
//   file_name: string;
//   original_name: string;
//   file_path: string;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface Artifact {
//   case_artifact_id: string;
//   assignment_template_id: string;
//   assignment_id: string;
//   case_id: number;
//   remarks: string;
//   followup_date: string;
//   created_by: string;
//   created_at: string;
//   documents: DocumentItem[];
// }

// interface ArtifactsApiResponse {
//   success: boolean;
//   data: Artifact[];
//   pagination?: {
//     totalRecords: number;
//   };
// }

// // breadcrumb data
// const breadcrumbItem = [{ label: "Case Details" }, { label: "View Artifacts" }];

// // DetailItem
// interface DetailItemProps {
//   label: string;
//   value: string;
//   placeholder?: string;
// }

// const DetailItem: React.FC<DetailItemProps> = ({
//   label,
//   value = "",
//   placeholder = "",
// }) => {
//   const isEmpty = !value || value.trim() === "";

//   return (
//     <div className="col-md-4 mb-3">
//       <label className="form-label fw-semibold" title={label}>
//         {label}
//       </label>
//       <input
//         type="text"
//         className="form-control"
//         value={isEmpty ? "" : value}
//         placeholder={isEmpty ? placeholder || "—" : ""}
//         disabled
//         title={value}
//       />
//     </div>
//   );
// };

// const AddArtifacts: React.FC = () => { 
//   const {
//     case_id: caseId,
//     assignment_id: assignId,
//     assignment_template_id: assingTempId,
//   } = useLocation()?.state || {};

//   const [formData, setFormData] = useState<any>({});

//   const [docsInfo, setDocsInfo] = useState<any[]>([]);

//   const [allItemList, setAllItemList] = useState<Artifact[]>([]);
//   const [selectedDocuments, setSelectedDocuments] = useState<DocumentItem[]>(
//     [],
//   );
//   const [modalOpen, setModalOpen] = useState<boolean>(false);
//   const [addressData, setAddressData] = useState<any[]>([]);
//   const [templateInfo, setTemplateInfo] = useState<any>(null);

//   const [pagination, setPagination] = useState<PaginationType>({
//     current: 1,
//     pageSize: 1000,
//     total: 0,
//   });

//   // API
//   const { callApi: getCaseDetailsAPI, loading: loading_case } = useApi<any>(
//     "",
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );
//   const { callApi: getArtifactsAPI, loading } = useApi<ArtifactsApiResponse>(
//     "",
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );
//   const getTemplateDocument = useApi("", {
//     autoCall: false,
//     method: "GET",
//     handleGlobalError: false,
//   });

//   // ========== fetch Check data ===========
//   const fetchCheckData = async () => {
//     try {
//       const res = await getCaseDetailsAPI(
//         undefined,
//         `${webservice_url.get_insufficiency_details}/${assingTempId}`,
//       );
//       if (res?.success) {
//         const data = res?.data || {};
//         setFormData(data);
//         // Store response details except fields
//         setTemplateInfo({
//           response_id: data.response_id,
//           assignment_template_id: data.assignment_template_id,
//           is_draft: data.is_draft,
//           submitted_at: data.submitted_at,
//           updated_at: data.updated_at,
//           check_name: data.check_name,
//           form_type: data.form_type,
//         });
//         setAddressData(Array.isArray(data.data) ? data.data : []);
//       }
//     } catch (err: any) {
//       console.error(err?.response);
//     }
//   };
//   useEffect(() => {
//     if (!assingTempId) return;
//     fetchCheckData();
//   }, []);
 
//   const fetchData = async () => {
//     try {
//       const res = await getArtifactsAPI(
//         undefined,
//         `${webservice_url.get_all_artifacts}/${assingTempId}`,
//       );

//       if (res?.success) {
//         const data = Array.isArray(res?.data) ? res.data : [];

//         setAllItemList(data);

//         setPagination((prev) => ({
//           ...prev,
//           total: res?.pagination?.totalRecords ?? data.length,
//         }));
//       } else {
//         setAllItemList([]);
//         setPagination((prev) => ({
//           ...prev,
//           total: 0,
//         }));
//       }
//     } catch (err: any) {
//       console.error(err?.response);

//       // Always keep Datatable dataSource as an array
//       setAllItemList([]);
//       setPagination((prev) => ({
//         ...prev,
//         total: 0,
//       }));
//     }
//   };
//   useEffect(() => {
//     if (!assingTempId) return;
//     fetchData();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [pagination.current, pagination.pageSize, caseId]);

//   const handleViewDocuments = (documents: DocumentItem[]) => {
//     setSelectedDocuments(documents || []);
//     setModalOpen(true);
//   };

//   const handleModalClose = () => {
//     setModalOpen(false);
//     setSelectedDocuments([]);
//   };

//   const handleViewDocument = (doc: DocumentItem) => {
//     let doc_link = doc?.file_path;
//     if (!doc_link) return;
//     window.open(doc_link, "_blank", "noopener,noreferrer");
//   };

//   const handleTableChange = (paginationConfig: TablePaginationConfig) => {
//     setPagination((prev) => ({
//       ...prev,
//       current: paginationConfig.current || 1,
//       pageSize: paginationConfig.pageSize || 10,
//     }));
//   };
//   // ========== fetch data ===========

//   // Fetch document
//   const getTemplateDocs = async () => {
//     try {
//       const res = await getTemplateDocument.callApi(
//         undefined,
//         `/api/v1/insufficiency/documents/${assingTempId}`,
//       );

//       if (res?.success) {
//         setDocsInfo(res.data || []);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };
//   useEffect(() => {
//     if (!assingTempId) return;
//     getTemplateDocs();
//   }, [assingTempId]);

//   const columns: ColumnsType<Artifact> = [
//     {
//       title: "Case ID",
//       dataIndex: "case_id",
//       width: 120,
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: "Remarks",
//       dataIndex: "remarks",
//       onCell: () => cellWidth(400),
//       render: (remarks: string) =>
//         remarks ? remarks : <span className="text-muted">-</span>,
//       sorter: (a: any, b: any) => safeLocaleCompare(a, b, "remarks"),
//     },
//     {
//       title: "Follow-up Date",
//       dataIndex: "followup_date",
//       render: (date: string) => formattedDate(date),
//       sorter: (a: any, b: any) =>
//         parseDate(a.followup_date).getTime() -
//         parseDate(b.followup_date).getTime(),
//     },
//     {
//       title: "Created At",
//       dataIndex: "created_at",
//       render: (date: string) => formattedDate(date),
//       sorter: (a: any, b: any) =>
//         parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime(),
//     },
//     {
//       title: "Documents",
//       width: 160,
//       render: (_: unknown, record: Artifact) => {
//         const count = record.documents?.length || 0;
//         if (count === 0) {
//           return <span className="text-muted">No documents</span>;
//         }
//         return (
//           <button
//             className="btn btn-sm btn-tertiary"
//             onClick={() => handleViewDocuments(record.documents)}
//           >
//             <i className="ti ti-eye"></i> View
//             {/* ({count}) */}
//           </button>
//         );
//       },
//     },
//   ];

//   // AddressDetailsView sirf "Address Verification" check par dikhega
//   const isAddressCheck =
//     templateInfo?.check_name?.trim().toLowerCase() === "address verification";

//  // Uploaded Documents card tabhi dikhega jab documents hon (aur address check na ho)
//   // 👇 ise `isAddressCheck` ke neeche, `return (` se pehle rakho
//   const showDocsCard =
//     !!templateInfo?.check_name && !isAddressCheck && docsInfo?.length > 0;

//   return (
//     <div className="page-wrapper">
//       <div className="content px-2 px-md-4 pb-5">
//         {loading_case || loading ? (
//           <PageLoader />
//         ) : (
//           <>
//             {/* PageBreadcrumb */}
//             <PageBreadcrumb
//               title="View Artifacts"
//               breadcrumbs={breadcrumbItem}
//               showBackButton={true}
//               backRoute={-1}
//               // backRoute={`${all_routes.admin_caseDetails}/${caseId}`}
//             />
//             {/* /PageBreadcrumb */}

//             <>
//               {/* artiface history */}
//               <div className="card mt-3">
//                 <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
//                   <h4 className="fw-bold mb-0">Artifacts History</h4>

//                   {!loading && (
//                     <span className="badge bg-light text-dark border px-3 py-2">
//                       {pagination.total}{" "}
//                       {pagination.total === 1 ? "record" : "records"}
//                     </span>
//                   )}
//                 </div>

//                 {/* table */}
//                 <div className="card-body">
//                   {loading ? (
//                     <div style={{ padding: "40px 0" }}>
//                       <Loading />
//                     </div>
//                   ) : (
//                     <Datatable
//                       rowKey="case_artifact_id"
//                       columns={columns}
//                       dataSource={allItemList}
//                       hideSizeChanger={true}
//                       pagination={{
//                         current: pagination.current,
//                         pageSize: pagination.pageSize,
//                         total: pagination.total,
//                       }}
//                       handleTableChange={handleTableChange}
//                     />
//                   )}
//                 </div>
//               </div>

//               {/* case details */}
//               <div className="card mt-3">
//                 <div className="card-header bg-white py-3 border-bottom">
//                   <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
//                     <div className="d-flex align-items-center">
//                       <div
//                         className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                         style={{ width: 40, height: 40 }}
//                       >
//                         <i className="ti ti-file-text text-warning fs-3"></i>
//                       </div>

//                       <div>
//                         <h6 className="fw-bold mb-0">{formData?.check_name}</h6>
//                         <small className="text-muted">
//                           Last Updated:{" "}
//                           {dayjs(formData?.updated_at).format(
//                             "DD MMM YYYY, hh:mm A",
//                           )}
//                         </small>
//                       </div>
//                     </div>

//                     {/* <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle px-3 py-2">
//                                         Cleared
//                                     </span> */}
//                   </div>
//                 </div>

//                 {isAddressCheck ? (
//                   <AddressDetailsView data={addressData} />
//                 ) : (
//                   <div className="card-body">
//                     <div className="row gx-3">
//                       {loading_case ? (
//                         <Loading />
//                       ) : (
//                         <>
//                           {/* <DetailItem
//                             label="Form Type"
//                             value={formData?.form_type
//                               ?.replaceAll("_", " ")
//                               .toUpperCase()}
//                           />
//                            */}
//                           {formData?.fields?.length > 0 &&
//                             formData?.fields
//                               ?.filter((f: any) =>
//                                 ["text", "number", "dropdown"]?.includes(
//                                   f?.field_type,
//                                 ),
//                               )
//                               ?.map((d: any) => (
//                                 <DetailItem
//                                   key={d?.field_id}
//                                   label={d?.label}
//                                   value={d?.value}
//                                   placeholder={d?.placeholder}
//                                 />
//                               ))}
//                         </>
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>

//               {/* Uploaded Documents — sirf tab dikhega jab documents hon */}
//               {showDocsCard ? (
//                 <div className="card mt-3">
//                   <div className="card-header bg-white py-3 border-bottom">
//                     <div className="d-flex align-items-center">
//                       <div
//                         className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle me-3"
//                         style={{ width: 40, height: 40 }}
//                       >
//                         <i className="ti ti-file-text text-warning fs-3"></i>
//                       </div>

//                       <div>
//                         <h6 className="fw-bold mb-0">Uploaded Documents</h6>
//                         <small className="text-muted">
//                           Candidate uploaded supporting documents
//                         </small>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="card-body">
//                     <div className="row g-3">
//                       {docsInfo.map((item) => (
//                         <div
//                           className="col-xl-3 col-lg-4 col-md-6"
//                           key={item.file_id}
//                         >
//                           <div
//                             className="card h-100 border-0 shadow-sm"
//                             style={{
//                               borderRadius: "14px",
//                               transition: "0.25s ease",
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.transform =
//                                 "translateY(-4px)";
//                               e.currentTarget.style.boxShadow =
//                                 "0 8px 20px rgba(0,0,0,0.12)";
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.transform = "translateY(0)";
//                               e.currentTarget.style.boxShadow = "";
//                             }}
//                           >
//                             <div className="card-body text-center">
//                               {/* Icon */}
//                               <div
//                                 className="mx-auto mb-3 d-flex align-items-center justify-content-center"
//                                 style={{
//                                   width: 70,
//                                   height: 70,
//                                   borderRadius: "50%",
//                                   background: "#FDECEC",
//                                 }}
//                               >
//                                 <PdfIcon size={42} color="#D32F2F" />
//                               </div>

//                               {/* Document Name */}
//                               <h6
//                                 className="fw-bold text-capitalize mb-2"
//                                 title={item.field_key.replaceAll("_", " ")}
//                               >
//                                 {item.field_key.replaceAll("_", " ")}
//                               </h6>

//                               {/* File Name */}
//                               <div className="small text-muted text-truncate mb-3">
//                                 {item.original_name}
//                               </div>

//                               {/* Info */}
//                               <div className="d-flex justify-content-between align-items-center mb-2">
//                                 <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                                   {item.mime_type?.split("/")[1]?.toUpperCase()}
//                                 </span>

//                                 <span className="badge bg-light text-dark border">
//                                   {new Date(item.uploaded_at).toLocaleDateString()}
//                                 </span>
//                               </div>

//                               {/* View Button */}
//                               <a
//                                 href={`${apibaseurl}/uploads/${item.s3_key}`}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="btn btn-sm btn-primary w-100 mt-2"
//                               >
//                                 <i className="ti ti-eye me-1"></i>
//                                 View Document
//                               </a>
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               ) : null}
//             </>
//           </>
//         )}

//         {/* view documents modal */}
//         <Modal
//           title={
//             <span className="fw-bold">
//               <i className="ti ti-folder-open me-2 text-primary"></i>
//               Documents
//             </span>
//           }
//           open={modalOpen}
//           onCancel={handleModalClose}
//           footer={null}
//           width={700}
//         >
//           {selectedDocuments.length === 0 ? (
//             <div className="text-center py-5">
//               <i
//                 className="ti ti-folder-off text-secondary"
//                 style={{ fontSize: "48px" }}
//               ></i>

//               <h6 className="mt-3 mb-1">No Documents Found</h6>

//               <p className="text-muted mb-0">No documents available</p>
//             </div>
//           ) : (
//             <div className="row g-3">
//               {selectedDocuments.map((doc) => (
//                 <div className="col-md-6" key={doc.document_id}>
//                   <div
//                     className="card h-100 border-0 shadow-sm"
//                     style={{
//                       borderRadius: "14px",
//                       transition: "0.25s ease",
//                     }}
//                     onMouseEnter={(e) => {
//                       e.currentTarget.style.transform = "translateY(-4px)";
//                       e.currentTarget.style.boxShadow =
//                         "0 8px 20px rgba(0,0,0,0.12)";
//                     }}
//                     onMouseLeave={(e) => {
//                       e.currentTarget.style.transform = "translateY(0)";
//                       e.currentTarget.style.boxShadow = "";
//                     }}
//                   >
//                     <div className="card-body">
//                       <div className="d-flex">
//                         {/* Icon */}
//                         <div
//                           className="d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle me-3"
//                           style={{
//                             width: 55,
//                             height: 55,
//                             flexShrink: 0,
//                           }}
//                         >
//                           <i className="ti ti-file-text text-danger fs-3"></i>
//                         </div>

//                         {/* Details */}
//                         <div className="flex-grow-1">
//                           <h6
//                             className="fw-bold mb-1 text-truncate"
//                             title={doc.original_name}
//                           >
//                             {doc.original_name}
//                           </h6>

//                           <div className="d-flex align-items-center gap-1 mb-1">
//                             <span className="text-muted small">Type:</span>
//                             <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
//                               {doc.mime_type}
//                             </span>
//                           </div>

//                           <div className="text-muted small mb-2">
//                             Uploaded: {formattedDate(doc.uploaded_at)}
//                           </div>
//                           <a
//                             href={`${apibaseurl}/uploads/${doc.file_name}`}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             className="btn btn-sm btn-primary"
//                           >
//                             <i className="ti ti-eye me-1"></i>
//                             View
//                           </a>
//                           {/* <button
//                                                         className="btn btn-sm btn-primary"
//                                                         onClick={() => handleViewDocument(doc)}
//                                                     >
//                                                         View
//                                                     </button> */}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           )}
//         </Modal>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default AddArtifacts; 